import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Home, BookOpen } from "lucide-react";
import "../../css/cogat/cogat.css";
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import FAQCOGATs from "./FAQCOGATs";
import CogatExamples from "./CogatExamples";

export default function CogatPage() {
  const navigate = useNavigate();

  const handleBackToHome = () => {
    navigate("/", { replace: false });
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="cogat-page-container">
      <Header />
      <main className="cogat-main-content">
      {/* Hero Section with SEO-optimized content */}
      <section className="cogat-hero-section">
        <div className="cogat-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="cogat-hero-text"
          >
            <h1 className="cogat-main-title">Cognitive Abilities Test (CoGAT) - Complete Guide & Practice Questions</h1>
            <p className="cogat-hero-description">
              The Cognitive Abilities Test (CoGAT) is a comprehensive assessment designed to evaluate a student's reasoning and problem-solving abilities across three key areas: verbal, quantitative, and nonverbal reasoning. Widely used by school districts for gifted and talented program placements, CoGAT helps identify students' cognitive strengths and potential for advanced learning opportunities.
            </p>
            <p className="cogat-hero-description">
              This free guide provides everything you need to understand the CoGAT test format, practice with sample questions from all three batteries, and prepare your child for success. All practice materials are completely free and designed specifically for elementary school students.
            </p>
          </motion.div>
        </div>
      </section>


      {/* Key Areas Section */}
      <section className="cogat-areas-section">
        <div className="cogat-areas-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="cogat-areas-content"
          >
            <h2 className="cogat-section-title">CoGAT Test Structure: Three Key Batteries</h2>
            <p className="cogat-section-intro">
              The CoGAT test is systematically organized into three main batteries, each designed to assess different types of reasoning abilities. Understanding these areas helps students and parents prepare effectively for the test.
            </p>

            {/* Verbal Battery */}
            <div className="cogat-battery-card">
              <h3 className="cogat-battery-title">
                <span className="battery-icon">🔠</span>
                Verbal Battery
              </h3>
              <p className="cogat-battery-description">
                The Verbal Battery evaluates a student's ability to understand and work with language-based concepts, relationships, and patterns. This section is crucial for assessing verbal reasoning skills.
              </p>
              <div className="cogat-subtest-list">
                <motion.div className="cogat-subtest-item" whileHover={{ scale: 1.02, x: 5 }}>
                  <strong>Picture/Verbal Analogies:</strong> Students identify relationships between images or words using a 2×2 matrix format, testing their ability to recognize patterns and logical connections.
                </motion.div>
                <motion.div className="cogat-subtest-item" whileHover={{ scale: 1.02, x: 5 }}>
                  <strong>Sentence Completion:</strong> Requires students to choose the best picture that logically completes a given sentence, assessing comprehension and logical thinking.
                </motion.div>
                <motion.div className="cogat-subtest-item" whileHover={{ scale: 1.02, x: 5 }}>
                  <strong>Picture/Verbal Classification:</strong> Students select the image or word that best fits with a given group, testing categorization and pattern recognition skills.
                </motion.div>
              </div>
            </div>

            {/* Quantitative Battery */}
            <div className="cogat-battery-card">
              <h3 className="cogat-battery-title">
                <span className="battery-icon">🔢</span>
                Quantitative Battery
              </h3>
              <p className="cogat-battery-description">
                The Quantitative Battery measures mathematical reasoning abilities, focusing on number relationships, patterns, and logical problem-solving without requiring advanced computational skills.
              </p>
              <div className="cogat-subtest-list">
                <motion.div className="cogat-subtest-item" whileHover={{ scale: 1.02, x: 5 }}>
                  <strong>Number Analogies:</strong> Students identify patterns and relationships between numbers, testing their ability to recognize mathematical relationships and logical sequences.
                </motion.div>
                <motion.div className="cogat-subtest-item" whileHover={{ scale: 1.02, x: 5 }}>
                  <strong>Number Puzzles:</strong> Requires solving puzzles using basic numerical logic and reasoning, assessing problem-solving abilities with numbers.
                </motion.div>
                <motion.div className="cogat-subtest-item" whileHover={{ scale: 1.02, x: 5 }}>
                  <strong>Number Series:</strong> Students recognize patterns in number sequences (often represented as bead strings) to determine the next number in the series.
                </motion.div>
              </div>
            </div>

            {/* Non-Verbal Battery */}
            <div className="cogat-battery-card">
              <h3 className="cogat-battery-title">
                <span className="battery-icon">📐</span>
                Non-Verbal Battery
              </h3>
              <p className="cogat-battery-description">
                The Non-Verbal Battery assesses spatial reasoning and visual problem-solving abilities, making it particularly valuable for students who may have language barriers or prefer visual learning.
              </p>
              <div className="cogat-subtest-list">
                <motion.div className="cogat-subtest-item" whileHover={{ scale: 1.02, x: 5 }}>
                  <strong>Figure Matrices:</strong> Uses spatial reasoning to determine relationships between geometric figures in a matrix format, testing visual pattern recognition.
                </motion.div>
                <motion.div className="cogat-subtest-item" whileHover={{ scale: 1.02, x: 5 }}>
                  <strong>Paper Folding:</strong> Students predict the outcome of folding and cutting paper, assessing spatial visualization and mental manipulation skills.
                </motion.div>
                <motion.div className="cogat-subtest-item" whileHover={{ scale: 1.02, x: 5 }}>
                  <strong>Figure Classification:</strong> Groups geometric figures based on shared visual characteristics, testing the ability to identify common patterns and relationships.
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Test Overview Section */}
      <section className="cogat-overview-section">
        <div className="cogat-overview-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="cogat-overview-content"
          >
            <h2 className="cogat-section-title">CoGAT Test Overview & Format</h2>
            <p className="cogat-overview-text">
              The Cognitive Abilities Test (CoGAT) is a standardized assessment that measures a student's reasoning and problem-solving abilities across verbal, quantitative, and non-verbal domains. The test provides valuable insights for academic placement, gifted program identification, and understanding a student's learning potential.
            </p>
            
            <div className="cogat-test-info-grid">
              <div className="cogat-info-card">
                <h4 className="info-card-title">⏱️ Test Duration</h4>
                <p className="info-card-text">60-75 minutes total, typically administered in multiple sessions</p>
              </div>
              <div className="cogat-info-card">
                <h4 className="info-card-title">📝 Test Format</h4>
                <p className="info-card-text">Primarily paper-based with multiple-choice questions, though some districts use online formats</p>
              </div>
              <div className="cogat-info-card">
                <h4 className="info-card-title">📊 Scoring System</h4>
                <p className="info-card-text">Results are scored using standard age norms and percentile rankings for comparison</p>
              </div>
              <div className="cogat-info-card">
                <h4 className="info-card-title">🎯 Grade Levels</h4>
                <p className="info-card-text">Typically administered to students in grades K-12, with age-appropriate question sets</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sample Questions Section */}
      <CogatExamples />

      {/* FAQ Section */}
      <FAQCOGATs />

      {/* Call to Action Section */}
      <section className="cogat-cta-section">
        <div className="cogat-cta-container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="cogat-cta-content"
          >
            <h2 className="cogat-cta-title">Ready to Practice for the CoGAT Test?</h2>
            <p className="cogat-cta-text">
              Access thousands of free CoGAT practice questions covering all three batteries. Create custom practice tests, track your progress, and download printable PDFs - all completely free!
            </p>
            <div className="cogat-cta-buttons">
              <a href="/account" className="cogat-cta-button cogat-cta-primary">
                <BookOpen size={20} />
                Start Practicing Free
              </a>
              <button onClick={handleBackToHome} className="cogat-cta-button cogat-cta-secondary">
                <Home size={20} />
                Back to Home
              </button>
            </div>
          </motion.div>
        </div>
      </section>
      </main>
      <Footer />
    </div>
  );
}
